import React from 'react';
import { Calendar, Clock, Bell, CheckCircle } from 'lucide-react';

const SchedulingSection = () => {
  const benefits = [
    {
      icon: Calendar,
      title: 'Plan Ahead',
      description: 'Schedule prints hours or days in advance'
    },
    {
      icon: Clock,
      title: 'Save Time',
      description: 'Skip waiting – your prints are ready when you arrive'
    },
    {
      icon: Bell,
      title: 'Get Notified',
      description: 'Receive alerts when your documents are ready'
    },
    {
      icon: CheckCircle,
      title: 'Pick Up Anytime',
      description: 'Collect your prints at your convenience'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-yellow-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <span className="inline-block px-4 py-2 bg-yellow-500 text-white rounded-full text-sm font-semibold mb-4">
                ⭐ Featured
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Schedule Your Prints in Advance
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Got a morning class but need prints? Schedule them the night before! 
                PRINTLEY's smart scheduling feature lets you upload documents and set a pickup time 
                that works for you. Perfect for busy students and professionals.
              </p>
            </div>

            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <benefit.icon className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4">
              <div className="bg-yellow-100 border-l-4 border-yellow-500 p-6 rounded-lg">
                <p className="text-gray-800 font-medium">
                  💡 <strong>Pro Tip:</strong> Schedule prints during off-peak hours for faster processing!
                </p>
              </div>
            </div>
          </div>

          {/* Right - Visual Demo */}
          <div className="relative">
            <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-gray-100">
              {/* Mock Phone Interface */}
              <div className="bg-gray-900 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold">Schedule Print</h3>
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                </div>

                {/* Document Info */}
                <div className="bg-gray-800 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-gray-900" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">Assignment_Final.pdf</p>
                      <p className="text-sm text-gray-400">12 pages • 2.4 MB</p>
                    </div>
                  </div>
                </div>

                {/* Print Options */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between bg-gray-800 rounded-xl p-4">
                    <span className="text-gray-300">Print Type</span>
                    <span className="font-semibold text-yellow-500">Black & White</span>
                  </div>
                  <div className="flex items-center justify-between bg-gray-800 rounded-xl p-4">
                    <span className="text-gray-300">Paper Size</span>
                    <span className="font-semibold">A4</span>
                  </div>
                </div>

                {/* Schedule Time Picker */}
                <div className="bg-gray-800 rounded-xl p-4 mb-6">
                  <label className="text-sm text-gray-400 mb-2 block">Pick Up Time</label>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-yellow-500" />
                    <input 
                      type="text" 
                      value="Tomorrow, 9:00 AM" 
                      readOnly
                      className="bg-transparent text-white font-semibold outline-none"
                    />
                  </div>
                </div>

                {/* Confirm Button */}
                <button className="w-full bg-yellow-500 text-gray-900 font-bold py-4 rounded-xl hover:bg-yellow-400 transition-colors duration-200">
                  Confirm & Pay ₹24
                </button>
              </div>

              {/* Notification Badge */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 border-2 border-yellow-200 animate-bounce">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-semibold text-gray-900">Ready in 12 hrs</span>
                </div>
              </div>
            </div>

            {/* Background Decoration */}
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-yellow-200/40 rounded-full blur-3xl -z-10"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Fix import
const FileText = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

export default SchedulingSection;
